import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import {
  MapPin,
  Clock,
  ChevronDown,
  Gift,
  Flower2,
  HelpCircle,
  Utensils,
  Flame,
  Heart,
  Sparkles,
} from "lucide-react";

import Header from "../Components/Header/Header.js";
import Footer from "../Components/Footer/Footer";
import Hero from "../Components/Hero";
import AccountDetails from "../Components/AccountDetails";
import FestivalHighlights from "../Components/FestivalHighlights";

// Banner & Asset Imports
import janmashtamiBanner from "../assets/JanmasthmiBanner.jpeg";
import divineDarshanImg from "../assets/Janmashmti/divineDarshan.webp";
import soulKirtanImg from "../assets/Janmashmti/SoulKirtan.webp";
import shankhabhishekamImg from "../assets/Janmashmti/Shankhabhishekam.webp";
import spiritualDiscoursesImg from "../assets/Janmashmti/SpiritualDiscourses.webp";
import pushpabhishekamImg from "../assets/Janmashmti/Pushpabhishekam.webp";
import uttiImg from "../assets/Janmashmti/utti.webp";
import kalashImg from "../assets/Janmashmti/Kalash.webp";
import anadaanThaliImg from "../assets/AnadaanThali.webp";
import adoptCowImg from "../assets/AdoptCow.webp";

const Janmashatmi = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    document.body.appendChild(script);
    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  const programSchedule = [
    {
      time: "04:30 AM – 05:15 AM",
      program: "Mangala Aarti",
      details: "Morning worship ushered in with classical kirtan.",
    },
    {
      time: "08:00 AM",
      program: "Special Sringar Darshan",
      details:
        "Unveiling of Sri Sri Radha Shyamasundara in festival Alankaram.",
    },
    {
      time: "09:00 AM – 09:00 PM",
      program: "Akhanda Harinama Kirtan",
      details: "Non-stop chanting of the Hare Krishna Mahamantra.",
    },
    {
      time: "06:00 PM – 09:00 PM",
      program: "Cultural Programs",
      details:
        "Classical dance, devotional music, and youth drama presentations.",
    },
    {
      time: "10:00 PM – 11:45 PM",
      program: "Midnight Maha Abhishekam",
      details:
        "Ceremonial bathing of Deities with milk, honey, ghee, and sacred juices.",
    },
    {
      time: "12:00 Midnight",
      program: "Maha Aarti & Birth Celebration",
      details:
        "Midnight celebration marking the divine appearance of Sri Krishna.",
    },
    {
      time: "12:30 AM Onward",
      program: "Mahaprasadam Distribution",
    },
  ];
  const festivalHighlights = [
    {
      id: "darshan",
      image: divineDarshanImg,
      title: "Divine Darshan",
      description:
        "Behold Lord Sri Radha Shyamsundar in His most exquisite and opulent form on this sacred day.",
    },
    {
      id: "kirtan",
      image: soulKirtanImg,
      title: "Soul-Stirring Kirtans",
      description:
        "Engage your soul in the uplifting waves of kirtans and bhajans led by senior Vaishnavas.",
    },
    {
      id: "shankhabhishekam",
      image: shankhabhishekamImg,
      title: "Grand Maha Shankhabhishekam",
      description:
        "Witness the ceremonial bathing of the Lord with sanctified conch water during the auspicious Maha...",
    },
    {
      id: "discourses",
      image: spiritualDiscoursesImg,
      title: "Spiritual Discourses",
      description:
        "Gain deep spiritual insights from renowned preachers narrating Krishna's pastimes.",
    },
    {
      id: "pushpa",
      image: pushpabhishekamImg,
      title: "Maha Pushpabhishekam",
      description:
        "Experience a breathtaking shower of fragrant fresh flower petals offered to the Lord.",
    },
    {
      id: "competitions",
      image: uttiImg,
      title: "Cultural Competitions",
      description:
        "Joyful celebrations including Utlotsavam (Dahi Handi), Gita chanting, and Vedic competitions.",
    },
    {
      id: "kalash",
      image: kalashImg,
      title: "108 Kalash Maha Abhishekam",
      description:
        "Sacred ceremonial bathing of Sri Sri Radha Shyamasundar with 108 sanctified holy Kalashas.",
    },
  ];

  const sevaCards = [
    {
      id: "anndaan",
      title: "Annadaan Seva",
      description:
        "Sanctified Prasadam meals distributed to visiting devotees on Sri Krishna Janmashtami.",
      amounts: [
        "1000 plates ₹ 60,000/-",
        "500 plates ₹ 30,000/-",
        "250 plates ₹ 15,000/-",
        "100 plates ₹ 6,000/-",
        "50 plates ₹ 3,000/-",
        "Custom",
      ],
      buttonLabel: "Annadaan",
      badge: "Prasadam Seva",
      icon: Utensils,
      image: anadaanThaliImg,
    },
    {
      id: "vastra_alankar",
      title: "Vastra Alankar Seva",
      description:
        "Contribute towards the exquisite handcrafted festival attire and jewellery for Sri Sri Radha Shyamasundara.",
      amounts: [
        "₹ 30,000/-",
        "₹ 20,000/-",
        "₹ 15,000/-",
        "₹ 5,000/-",
        "Custom",
      ],
      buttonLabel: "Vastra Alankar",
      badge: "Grand Seva",
      icon: Gift,
      image: divineDarshanImg,
    },
    {
      id: "maha_nivedana",
      title: "Maha Nivedana Seva (Chappan Bhog)",
      description:
        "Offer 56 varieties of royal devotional delicacies and Rajbhog offerings to the Deities.",
      amounts: ["₹ 50,000/- day", "₹ 15,000/- day", "₹ 10,000/- day", "Custom"],
      buttonLabel: "Maha Nivedana",
      badge: "Chappan Bhog",
      icon: Sparkles,
      image: kalashImg,
    },
    {
      id: "maha_shanka_abhisheka",
      title: "Maha Shanka Abhisheka",
      description:
        "Participate in the auspicious ceremonial bathing of the Lord with sanctified conch shells.",
      amounts: [
        "₹ 5,000/- day",
        "₹ 3,000/- day",
        "₹ 1,000/- day",
        "₹ 500/- day",
        "Custom",
      ],
      buttonLabel: "Shanka Abhisheka",
      badge: "Maha Abhisheka",
      icon: Flame,
      image: shankhabhishekamImg,
    },
    {
      id: "gau_seva",
      title: "Gau Seva",
      description:
        "Serve and protect the sacred cows of ISKCON Goshala with fodder, medical care, and shelter.",
      amounts: ["₹ 5,000/-", "₹ 3,000/-", "₹ 1,000/-", "₹ 500", "Custom"],
      buttonLabel: "Gau Seva",
      badge: "Goshala Seva",
      icon: Heart,
      image: adoptCowImg,
    },
    {
      id: "pushpanjali",
      title: "Pushpanjali Seva",
      description:
        "Offer fresh fragrant flower showers and floral decorations at the lotus feet of the Lord.",
      amounts: ["₹ 5,000/-", "₹ 3,000/-", "₹ 1,000/-", "₹ 500/-", "Custom"],
      buttonLabel: "Pushpanjali",
      badge: "Floral Offering",
      icon: Flower2,
      image: pushpabhishekamImg,
    },
  ];

  // Helper to extract numeric price from formatted text (e.g. "1000 plates ₹ 60,000/-" -> 60000)
  const extractPrice = (amt) => {
    if (typeof amt === "number") return amt;
    if (!amt || amt === "Custom") return 0;
    const match = amt.match(/₹\s*([\d,]+)/);
    if (match) {
      return parseFloat(match[1].replace(/,/g, "")) || 0;
    }
    return parseFloat(amt.replace(/[^\d.]/g, "")) || 0;
  };

  // Donations, Selection & Modal State
  const [donations, setDonations] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [customAmounts, setCustomAmounts] = useState({});

  // Track selected amount for each card (default to first amount)
  const [selectedAmounts, setSelectedAmounts] = useState({
    anndaan: "1000 plates ₹ 60,000/-",
    vastra_alankar: "₹ 5,000/-",
    maha_nivedana: "₹ 50,000/- day",
    maha_shanka_abhisheka: "₹ 5,000/- day",
    gau_seva: "₹ 5,000/-",
    pushpanjali: "₹ 5,000/-",
  });

  const [expandedMobileCards, setExpandedMobileCards] = useState({});

  const toggleMobileCard = (cardId) => {
    setExpandedMobileCards((prev) => ({
      ...prev,
      [cardId]: !prev[cardId],
    }));
  };

  const handleAmountSelect = (cardId, amt, card) => {
    setSelectedAmounts((prev) => ({
      ...prev,
      [cardId]: amt,
    }));

    if (amt !== "Custom") {
      const numericPrice = extractPrice(amt);
      const label = card.title;
      setDonations((prev) => {
        const isAdded = prev.some((d) => d.cardId === cardId);
        if (isAdded) {
          return prev.map((d) =>
            d.cardId === cardId ? { ...d, label, price: numericPrice } : d,
          );
        }
        return prev;
      });
    }
  };

  const handleCustomAmountChange = (cardId, value, card) => {
    setCustomAmounts((prev) => ({ ...prev, [cardId]: value }));
    const numericPrice = parseFloat(value) || 0;
    if (numericPrice > 0) {
      const label = card.title;
      setDonations((prev) => {
        const isAdded = prev.some((d) => d.cardId === cardId);
        if (isAdded) {
          return prev.map((d) =>
            d.cardId === cardId ? { ...d, label, price: numericPrice } : d,
          );
        }
        return prev;
      });
    }
  };

  const handleCardToggle = (card) => {
    const isCustom = selectedAmounts[card.id] === "Custom";
    let numericPrice = 0;
    let amountText = selectedAmounts[card.id] || card.amounts[0];

    if (isCustom) {
      numericPrice = parseFloat(customAmounts[card.id]) || 0;
      if (!numericPrice || numericPrice <= 0) {
        alert("Please enter a valid custom donation amount in ₹.");
        return;
      }
    } else {
      numericPrice = extractPrice(amountText);
    }

    const label = card.title;
    const img = card.image || divineDarshanImg;

    setDonations((prev) => {
      const exists = prev.some((d) => d.cardId === card.id);
      if (exists) {
        return prev.filter((d) => d.cardId !== card.id);
      }
      return [...prev, { label, price: numericPrice, img, cardId: card.id }];
    });
  };

  const toggleDonation = (label, price, img) => {
    setDonations((prev) =>
      prev.find((d) => d.label === label)
        ? prev.filter((d) => d.label !== label)
        : [...prev, { label, price, img }],
    );
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    if (name === "phone" && !/^\d*$/.test(value)) return;
    if (name === "phone" && value.length > 10) return;
    setForm({ ...form, [name]: value });
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const totalAmount = donations.reduce((sum, d) => sum + d.price, 0);

  const firstLabel = donations[0]?.label || "";
  const extraCount = donations.length - 1;
  const displayLabel =
    donations.length === 0
      ? ""
      : extraCount > 0
        ? `${firstLabel} + ${extraCount} more`
        : firstLabel;

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleContinueToPay = async () => {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = "Name is required.";
    if (!form.phone) newErrors.phone = "Phone number is required.";
    else if (form.phone.length !== 10)
      newErrors.phone = "Enter a valid 10-digit phone number.";
    if (!form.email.trim()) newErrors.email = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      newErrors.email = "Enter a valid email address.";
    if (!form.address.trim()) newErrors.address = "Address is required.";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      alert(
        "Please fill all required fields correctly (Name, 10-digit Phone, Email, Address).",
      );
      return;
    }

    if (totalAmount <= 0) {
      alert("Please select at least one donation seva amount.");
      return;
    }

    try {
      setLoading(true);

      // Ensure Razorpay SDK is loaded
      const isRzpLoaded = await loadRazorpayScript();
      if (!isRzpLoaded || !window.Razorpay) {
        alert(
          "Payment gateway failed to load. Please check your internet connection and try again.",
        );
        return;
      }

      const sevaSummary = donations
        .map(
          (d, idx) =>
            `${idx + 1}. ${d.label} - ₹${d.price.toLocaleString("en-IN")}`,
        )
        .join("\n");

      const apiUrl =
        process.env.REACT_APP_API_URL || "https://www.iskconvijayawada.org/api";

      const res = await axios.post(`${apiUrl}/api/payment/create-order`, {
        amount: totalAmount,
        currency: "INR",
        receipt: `rcpt_janm_${Date.now().toString().slice(-8)}`,
        notes: {
          festival: "Sri Krishna Janmashtami 2026",
          donor_name: form.name,
          donor_phone: form.phone,
          donor_email: form.email,
          donor_address: form.address.slice(0, 100),
          seva_details: sevaSummary.slice(0, 250),
        },
      });

      const order = res.data;
      if (!order || !order.id) {
        throw new Error("Failed to create order ID from payment server.");
      }

      const keyId = process.env.REACT_APP_RAZORPAY_KEY_ID;

      const options = {
        key: keyId,
        amount: Math.round(totalAmount * 100),
        currency: "INR",
        name: "ISKCON Vijayawada",
        description: `Janmashtami 2026 Donation`,
        order_id: order.id,
        notes: {
          festival: "Sri Krishna Janmashtami 2026",
          donor_name: form.name,
          donor_phone: form.phone,
          donor_email: form.email,
          donor_address: form.address,
          donation_summary: sevaSummary.slice(0, 250),
        },

        handler: async function (response) {
          try {
            const verifyRes = await axios.post(
              `${apiUrl}/api/payment/verify-payment`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                donationDetails: {
                  festival: "Sri Krishna Janmashtami 2026",
                  amount: totalAmount,
                  donor: form,
                  items: donations.map((d) => ({
                    label: d.label,
                    price: d.price,
                  })),
                },
              },
            );

            if (verifyRes.data?.success) {
              await storeFormData("Success", response);
              alert(
                "Payment successful! Thank you for your divine seva to Sri Sri Radha Shyamasundara.",
              );
              setShowModal(false);
            } else {
              await storeFormData("Verification Failed", response);
              alert(
                "Payment verification issue. Please contact temple administration.",
              );
            }
          } catch (err) {
            console.error("Verification error:", err);
            await storeFormData("Payment Failed", response);
            alert("Payment verification failed. Please contact support.");
          }
        },
        prefill: {
          name: form.name,
          email: form.email,
          contact: `+91${form.phone}`,
        },
        theme: { color: "#C61D21" },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
      rzp.on("payment.failed", async function (response) {
        alert(
          "Payment Failed: " +
            (response.error?.description || "Transaction failed"),
        );
      });
    } catch (err) {
      console.error("Payment initialization error:", err);
      const serverErr =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        "Server connection error. Please check your backend connection.";
      alert("Unable to open Razorpay: " + serverErr);
    } finally {
      setLoading(false);
    }
  };

  const storeFormData = async (paymentStatus, paymentResponse = {}) => {
    const storingFormData = {
      PaymentStatus: paymentStatus,
      PaymentId: paymentResponse.razorpay_payment_id || "N/A",
      OrderId: paymentResponse.razorpay_order_id || "N/A",
      Name: form.name,
      Email: form.email,
      Number: form.phone,
      Address: form.address,
      Festival: "Sri Krishna Janmashtami 2026",
      Seva: donations
        .map((d) => `${d.label} (₹${d.price.toLocaleString("en-IN")})`)
        .join("\n"),
      Items: JSON.stringify(
        donations.map((d) => ({
          seva: d.label,
          amount: d.price,
        })),
      ),
      Amount: totalAmount,
      Date: new Date().toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }),
    };
    const formBody = new URLSearchParams(storingFormData).toString();

    try {
      await fetch(
        "https://script.google.com/macros/s/AKfycbw-78K35NrfVUzxPrA301fugLNA4U3Qe6Q0gyF7ez6DdNIUU6wmehd7-gGz_yWrQ4n0/exec",
        {
          method: "POST",
          body: formBody,
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
        },
      );
      setForm({ name: "", phone: "", email: "", address: "" });
      setDonations([]);
      setCustomAmounts({});
    } catch (error) {
      console.error("Form submission error:", error);
    }
  };

  // FAQ State & Data
  const faqItems = [
    {
      question: "What is Janmashtami?",
      answer:
        "Janmashtami is the auspicious appearance day of Lord Sri Krishna, celebrated with devotional singing, cultural programs, abhishekam, arati, and midnight celebrations.",
    },
    {
      question: "Is there any entry fee?",
      answer:
        "No. Entry is absolutely free. Everyone is welcome to participate in the celebrations and receive the blessings of Lord Krishna.",
    },
    {
      question: "What time should I visit?",
      answer:
        "You can visit anytime during the celebrations. However, the main attractions, cultural programs, and the midnight Maha Arati are the highlights of the festival.",
    },
    {
      question: "Is prasadam available?",
      answer:
        "Yes. Delicious Krishna Prasadam will be distributed to all devotees after offering it to the Lord.",
    },
    {
      question: "Can I bring my family and children?",
      answer:
        "Absolutely! Janmashtami is a family-friendly festival with devotional activities, bhajans, cultural performances, and a joyful spiritual atmosphere for people of all ages. Hare Krishna! 🙏",
    },
  ];

  const [openFaqIndex, setOpenFaqIndex] = useState(0);

  const toggleFaq = (index) => {
    setOpenFaqIndex((prev) => (prev === index ? null : index));
  };

  return (
    <div
      className={`min-h-screen bg-white text-slate-800 font-sans ${
        donations.length > 0 ? "pb-44 md:pb-20" : "pb-16 md:pb-0"
      }`}
    >
      <Helmet>
        <title>
          Sri Krishna Janmashtami 2026 | ISKCON Vijayawada Festival &amp; Seva
        </title>
        <meta
          name="description"
          content="Celebrate Sri Krishna Janmashtami 2026 at ISKCON Vijayawada. Check darshan timings, midnight Abhishekam schedule, and online 80G tax-exempt Seva."
        />
        <meta
          name="keywords"
          content="Janmashtami Vijayawada ISKCON, Krishna Janmashtami 2026 Vijayawada, ISKCON Janmashtami donation, Janmashtami darshan timings Vijayawada"
        />
      </Helmet>

      {/* Header */}
      <Header />

      {/* HERO SECTION COMPONENT */}
      <Hero image={janmashtamiBanner} />

      {/* ==================== SECTION 1: FESTIVAL INTRO ==================== */}
      <div className="pt-12 sm:pt-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          {/* Top Badge */}
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-darkred/10 border border-red text-red text-xs sm:text-sm font-semibold tracking-wide uppercase mb-6">
            <span>Sri Krishna Janmashtami 2026</span>
          </div>

          {/* H1 Heading */}
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-darkred tracking-tight leading-tight mb-6">
            Sri Krishna Janmashtami 2026 at ISKCON Vijayawada
          </h1>

          {/* Paragraph */}
          <p className="text-base sm:text-lg md:text-xl text-slate-700 leading-relaxed font-normal mb-8">
            Join us at ISKCON Vijayawada for the grandest event of the year -
            <strong className="text-red font-semibold">
              Sri Krishna Janmashtami
            </strong>
            ! Experience a day filled with continuous{" "}
            <em className="text-red font-medium not-italic">
              Hari Nama Kirtan
            </em>
            , grand{" "}
            <em className="text-red font-medium not-italic">Maha Abhishekam</em>
            , cultural performances, and midnight{" "}
            <em className="text-red font-medium not-italic">Mahaprasadam</em>{" "}
            distribution across both our{" "}
            <strong className="text-red font-semibold">
              SRI SRI RADHA SHYAMSUNDAR MANDIR
            </strong>{" "}
            and{" "}
            <strong className="text-red font-semibold">
              SRI SRI JAGANNATH MANDIR
            </strong>
            .
          </p>
        </div>
      </div>

      {/* ==================== SECTION: FESTIVAL HIGHLIGHTS ==================== */}
      <FestivalHighlights
        title="Festival Highlights"
        items={festivalHighlights}
        className="-mt-2 mb-4"
      />

      {/* ==================== SECTION: SEVA DONATION CARDS (80G TAX EXEMPTION) ==================== */}
      <div className="pb-6 sm:pb-14 px-2 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header & 80G Exemption Badge */}
          <div className="text-center max-w-3xl mx-auto mb-10">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-red tracking-tight leading-tight">
              Janmashtami Seva &amp; Online Donations
            </h2>

            <p className="text-base sm:text-lg text-slate-700 mt-4 leading-relaxed text-center">
              Receive the divine blessings of Sri Sri Radha Shyamasundara on
              this auspicious day by supporting festival services.
            </p>
          </div>

          {/* SEVA_DONATION_CARDS GRID (3 CARDS PER ROW) */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2.5 md:gap-6">
            {sevaCards.map((card) => {
              const CardIcon = card.icon;
              const currentSelected =
                selectedAmounts[card.id] || card.amounts[0];
              const isAdded = donations.some((d) => d.cardId === card.id);
              const isExpandedMobile =
                !!expandedMobileCards[card.id] || isAdded;

              return (
                <div
                  key={card.id}
                  className={`group bg-white border rounded-3xl p-3 sm:p-6 shadow-sm hover:shadow-xl hover:-translate-y-1.5 transition-all duration-300 flex flex-col justify-between ${
                    isAdded
                      ? "border-red ring-2 ring-red/20 shadow-md"
                      : "border-slate-200 hover:border-red"
                  }`}
                >
                  <div>
                    {/* Card Top / Header (Clickable on mobile to toggle open/close) */}
                    <div
                      onClick={() => toggleMobileCard(card.id)}
                      className="cursor-pointer md:cursor-default select-none"
                    >
                      {/* Card Top Pill & Icon */}
                      <div className="flex items-center justify-between mb-2 sm:mb-4">
                        <span className="px-3 py-1 rounded-full bg-darkred/10 border border-red text-red text-xs font-semibold uppercase tracking-wider">
                          {card.badge}
                        </span>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-darkred/10 border border-red flex items-center justify-center text-red group-hover:bg-red group-hover:text-white transition-all duration-300">
                            <CardIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                          </div>
                          {/* Mobile Arrow Indicator */}
                          <div className="md:hidden w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center text-slate-700">
                            <ChevronDown
                              className={`w-4 h-4 text-red transition-transform duration-300 ${
                                isExpandedMobile ? "rotate-180" : "rotate-0"
                              }`}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Title & Description */}
                      <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-0 md:mb-2">
                        {card.title}
                      </h3>
                      <p className="hidden md:block text-slate-600 text-xs sm:text-sm leading-relaxed mb-3 sm:mb-6">
                        {card.description}
                      </p>
                    </div>

                    {/* Expandable Section: Amounts and CTA Button (always open on desktop, toggleable on mobile) */}
                    <div
                      className={`${
                        isExpandedMobile ? "block" : "hidden md:block"
                      } mt-3 md:mt-0 pt-3 md:pt-0 border-t md:border-t-0 border-slate-100`}
                    >
                      {/* Amount Options Chips */}
                      <div className="mb-4 sm:mb-6">
                        <span className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-2.5">
                          Select Donation Amount
                        </span>
                        <div className="grid grid-cols-2 gap-2.5">
                          {card.amounts.map((amt) => {
                            const isSelected = currentSelected === amt;
                            return (
                              <button
                                key={amt}
                                type="button"
                                onClick={() =>
                                  handleAmountSelect(card.id, amt, card)
                                }
                                className={`py-2.5 px-3 text-center rounded-xl text-xs sm:text-sm font-semibold transition-all duration-200 leading-snug ${
                                  isSelected
                                    ? "bg-red text-white shadow-sm ring-2 ring-red"
                                    : "bg-slate-100 text-slate-700 hover:bg-darkred/10 hover:text-red"
                                }`}
                              >
                                {amt}
                              </button>
                            );
                          })}
                        </div>

                        {currentSelected === "Custom" && (
                          <div className="mt-3">
                            <input
                              type="number"
                              value={customAmounts[card.id] || ""}
                              onChange={(e) =>
                                handleCustomAmountChange(
                                  card.id,
                                  e.target.value,
                                  card,
                                )
                              }
                              placeholder="Enter custom amount in ₹"
                              className="w-full py-2.5 px-4 rounded-xl bg-white border border-slate-300 text-slate-900 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-red shadow-sm"
                            />
                          </div>
                        )}
                      </div>

                      {/* CTA Button */}
                      <button
                        type="button"
                        onClick={() => handleCardToggle(card)}
                        className={`w-full inline-flex items-center justify-center py-3.5 px-6 rounded-2xl font-bold text-sm sm:text-base shadow-sm transition-all duration-200 active:scale-[0.98] ${
                          isAdded
                            ? "bg-gray-500 hover:bg-gray-600 text-white"
                            : "bg-red hover:bg-darkred text-white"
                        }`}
                      >
                        <span>
                          {isAdded
                            ? "Remove Seva"
                            : `${card.buttonLabel} (${
                                currentSelected === "Custom"
                                  ? customAmounts[card.id]
                                    ? `₹${Number(customAmounts[card.id]).toLocaleString("en-IN")}`
                                    : "Custom"
                                  : currentSelected
                              })`}
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div id="account-details">
        <AccountDetails />
      </div>
      {/* Gentle Request alert */}
      <div className="mx-4 md:mx-12 my-6 p-6 border-2 border-rose-400 shadow-md shadow-rose-100/40 rounded-lg shadow-sm">
        <div className="flex items-start">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-rose-600 mb-2">
              Gentle Request!
            </h3>
            <p className="text-gray-700 leading-relaxed text-sm m-0">
              While doing Paytm/UPI App Payments or Bank (NEFT/RTGS) please send
              us a screenshot along with complete address and PAN details on our
              contact details: WhatsApp{" "}
              <span className="text-blue-600 hover:text-blue-800 font-medium cursor-pointer">
                +91-9955685568{" "}
              </span>
              Email:{" "}
              <span className="text-blue-600 hover:text-blue-800 font-medium cursor-pointer">
                seva@iskconvijayawada.org{" "}
              </span>
              You may also call on the above number for other queries.
            </p>
          </div>
        </div>
      </div>

      {/* ==================== SECTION: FESTIVAL PROGRAM SCHEDULE TABLE ==================== */}
      <div className="pb-6 sm:pb-14 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          {/* H2 Heading */}
          <div className="mb-8 text-center sm:text-left">
            <h2 className="text-2xl sm:text-3xl font-bold text-red tracking-tight">
              Festival Program Schedule
            </h2>
            <p className="text-slate-600 text-sm sm:text-base mt-2">
              Complete timeline of divine rituals, kirtan, Abhishekam, and
              celebrations.
            </p>
          </div>

          {/* SCHEDULE COMPONENT: Mobile Card View & Desktop Table */}
          {/* MOBILE TIMELINE VIEW (Visible on mobile only) */}
          <div className="md:hidden space-y-2.5">
            {programSchedule.map((row, idx) => (
              <div
                key={idx}
                className="bg-white border border-red/20 rounded-2xl p-3.5 shadow-sm relative"
              >
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-darkred/10 border border-red/20 text-red text-xs font-bold">
                    <Clock className="w-3.5 h-3.5 text-red shrink-0" />
                    <span>{row.time}</span>
                  </div>
                  <span className="w-5 h-5 rounded-full bg-slate-100 text-slate-500 text-[10px] flex items-center justify-center font-bold">
                    {idx + 1}
                  </span>
                </div>
                <h3 className="text-sm font-bold text-slate-900">
                  {row.program}
                </h3>
                {row.details && (
                  <p className="text-xs text-slate-600 leading-relaxed mt-1 mb-0">
                    {row.details}
                  </p>
                )}
              </div>
            ))}
          </div>

          {/* DESKTOP TABLE VIEW (Visible on tablet & desktop only) */}
          <div className="hidden md:block overflow-hidden rounded-2xl border-1 border-red bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse min-w-[520px]">
                <thead>
                  <tr className="bg-darkred/10 border-b border-red text-red text-xs sm:text-sm font-bold uppercase tracking-wider">
                    <th scope="col" className="py-3 px-3 sm:py-4 sm:px-6 w-1/4">
                      Time
                    </th>
                    <th scope="col" className="py-3 px-3 sm:py-4 sm:px-6 w-1/3">
                      Ritual / Program
                    </th>
                    <th
                      scope="col"
                      className="py-3 px-3 sm:py-4 sm:px-6 w-5/12"
                    >
                      Details
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-xs sm:text-sm md:text-base text-slate-700">
                  {programSchedule.map((row, idx) => (
                    <tr
                      key={idx}
                      className="hover:bg-slate-50 transition-colors duration-150"
                    >
                      <td className="py-3 px-3 sm:py-4 sm:px-6 font-semibold text-slate-900 bg-slate-50/50 whitespace-nowrap">
                        <div className="flex items-center gap-1.5 sm:gap-2">
                          <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-red shrink-0" />
                          <span>{row.time}</span>
                        </div>
                      </td>
                      <td className="py-3 px-3 sm:py-4 sm:px-6 font-bold text-red">
                        {row.program}
                      </td>
                      <td className="py-3 px-3 sm:py-4 sm:px-6 leading-relaxed font-normal text-slate-800">
                        {row.details}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      {/* ==================== SECTION: FREQUENTLY ASKED QUESTIONS (FAQ) ==================== */}
      <div className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center sm:text-left mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-darkred/10 border border-red text-red text-xs font-semibold uppercase tracking-wider mb-3">
              <HelpCircle className="w-3.5 h-3.5 text-red" />
              <span>Devotee Assistance</span>
            </div>

            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 tracking-tight">
              Frequently Asked Questions (FAQ)
            </h2>
            <p className="text-slate-600 text-sm sm:text-base mt-2">
              Find quick answers to common queries regarding Janmashtami
              festival Seva, tax receipts, and temple facilities.
            </p>
          </div>

          {/* ACCORDION COMPONENT */}
          <div className="space-y-4">
            {faqItems.map((item, idx) => {
              const isOpen = openFaqIndex === idx;

              return (
                <div
                  key={idx}
                  className={`border rounded-2xl transition-all duration-200 overflow-hidden ${
                    isOpen
                      ? "bg-white border-red shadow-sm ring-1 ring-red/30"
                      : "bg-white border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => toggleFaq(idx)}
                    className="w-full py-4 px-6 sm:px-8 text-left font-bold text-slate-900 text-base sm:text-lg flex items-center justify-between gap-4 focus:outline-none"
                  >
                    <span className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-darkred/10 text-red text-xs flex items-center justify-center font-extrabold shrink-0">
                        {idx + 1}
                      </span>
                      <span>{item.question}</span>
                    </span>
                    <ChevronDown
                      className={`w-5 h-5 text-red shrink-0 transition-transform duration-300 ${
                        isOpen ? "rotate-180" : "rotate-0"
                      }`}
                    />
                  </button>

                  {isOpen && (
                    <div className="px-6 sm:px-8 pb-5 pt-1 text-slate-700 text-sm sm:text-base leading-relaxed border-t border-slate-100 bg-slate-50/50">
                      {item.answer}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {/* ==================== SECTION 2: TEMPLE LOCATIONS & VENUES ==================== */}
      <div className="py-8 sm:py-10 px-4 sm:px-6 lg:px-8 ">
        <div className="max-w-5xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="text-2xl sm:text-3xl text-red font-bold  tracking-tight">
              Celebration Venues
            </h2>
            <p className="text-slate-600 text-sm sm:text-base mt-2">
              Visit either of our sacred temples in Vijayawada to participate in
              the festival.
            </p>
          </div>

          {/* Dual Venues Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Undavalli Main Temple */}
            <div className="group bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 shadow-sm hover:shadow-lg hover:-translate-y-1 hover:border-red transition-all duration-300">
              <div className="flex items-start">
                <p className="px-3 py-1 rounded-full bg-darkred/10 text-red text-xs font-semibold uppercase tracking-wider">
                  Main Shrine
                </p>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2 group-hover:text-red transition-colors duration-200">
                SRI RADHA SHYAM SUNDAR MANDIR
              </h3>
              <p className="text-slate-600 text-sm mb-4 leading-relaxed">
                Grand Midnight Maha Abhishekam, continuous Kirtan, cultural
                stage performances, and mega Annadaan feast arena.
              </p>
              <div className="flex items-center justify-between pt-4 border-t border-slate-100 text-xs sm:text-sm">
                <span className="flex items-center gap-1.5 text-red font-medium">
                  <MapPin className="w-4 h-4 text-red" />
                  Main Temple - Amaravathi Karakatta Road, Undavalli,
                  Vijayawada, 522 501
                </span>
              </div>
            </div>

            {/* Krishna Lanka City Temple */}
            <div className="group bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 shadow-sm hover:shadow-lg hover:-translate-y-1 hover:border-red transition-all duration-300">
              <div className="flex items-start">
                <p className="px-3 py-1 rounded-full bg-darkred/10 text-red text-xs font-semibold uppercase tracking-wider">
                  City Center
                </p>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2 group-hover:text-red transition-colors duration-200">
                SRI JAGANNATH MANDIR
              </h3>
              <p className="text-slate-600 text-sm mb-4 leading-relaxed">
                City center Darshan, special Arati, continuous Prasadam
                distribution, and devotional book exhibition.
              </p>
              <div className="flex items-center justify-between pt-4 border-t border-slate-100 text-xs sm:text-sm">
                <span className="flex items-center gap-1.5 text-red font-medium">
                  <MapPin className="w-4 h-4 text-red" />
                  City Temple - Skew Bridge, Krishna Lanka, Vijayawada - 520013
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Footer */}
      <Footer />

      {/* Sticky Donation Bar */}
      {donations.length > 0 && (
        <div className="fixed bottom-[64px] md:bottom-0 left-0 right-0 z-[1020] bg-gray-300 md:bg-gray-200 text-slate-900 border-t border-slate-400 md:border-t-0 px-3 sm:px-8 py-2.5 sm:py-3.5 flex items-center justify-between gap-3 shadow-[0_-4px_25px_rgba(0,0,0,0.25)]">
          {/* Left: Donation summary */}
          <div className="flex flex-col min-w-0 flex-1">
            <span className="text-[10px] sm:text-xs text-slate-600 font-semibold uppercase tracking-wider">
              Donating for
            </span>
            <span className="text-xs sm:text-sm font-bold text-slate-900 truncate underline">
              {displayLabel}
            </span>
            <span className="text-xs font-extrabold text-red sm:hidden mt-0.5">
              Total: ₹{totalAmount.toLocaleString("en-IN")}
            </span>
          </div>

          {/* Right: Amount & Pay Button */}
          <div className="flex items-center gap-3 sm:gap-6 flex-shrink-0">
            <div className="text-right hidden sm:block">
              <p className="text-xs text-slate-500 font-medium">
                Total Amount:
              </p>
              <p className="text-base sm:text-lg font-bold text-slate-900 leading-tight">
                ₹{" "}
                {totalAmount.toLocaleString("en-IN", {
                  minimumFractionDigits: 2,
                })}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowModal(true)}
              className="bg-red hover:bg-darkred text-white font-bold px-4 sm:px-8 py-2.5 sm:py-3 rounded-xl text-xs sm:text-base transition-all whitespace-nowrap shadow-md active:scale-95 flex items-center gap-1.5"
            >
              <span>Pay Now</span>
            </button>
          </div>
        </div>
      )}

      {/* Pay Now Modal */}
      {showModal && (
        <div className="fixed inset-0 z-[1060] bg-black/75 overflow-y-auto p-3 sm:p-6 flex items-center justify-center">
          <div className="bg-gray-200 rounded-2xl w-full max-w-3xl max-h-[90vh] flex flex-col p-4 sm:p-6 relative shadow-2xl my-auto">
            {/* Close */}
            <button
              type="button"
              onClick={() => setShowModal(false)}
              className="absolute top-3 right-3 sm:top-4 sm:right-4 bg-white hover:bg-gray-100 text-slate-800 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shadow-md transition-colors z-20"
            >
              ✕
            </button>

            {/* Title */}
            <h2 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-5 flex items-center gap-2 text-slate-900 pr-10 shrink-0">
              <span className="w-1 h-6 sm:h-7 bg-red inline-block rounded"></span>
              Donate with love
            </h2>

            {/* Scrollable Form & Details Body */}
            <div className="overflow-y-auto flex-1 pr-1 pb-2 flex flex-col gap-4">
              <div className="flex flex-col md:flex-row gap-4">
                {/* Form */}
                <div className="flex-1 border border-slate-200 bg-white rounded-xl py-3 sm:py-4 px-3.5 sm:px-4 flex flex-col gap-2 shadow-sm">
                  <p className="text-xs sm:text-sm font-semibold text-slate-700">
                    Please fill required fields to continue
                  </p>
                  <div className="flex flex-col gap-1">
                    <input
                      name="name"
                      value={form.name}
                      onChange={handleFormChange}
                      placeholder="Your Legal Name"
                      className={`bg-slate-50 border rounded-lg px-3.5 py-2 sm:py-2.5 text-xs sm:text-sm placeholder-gray-400 focus:outline-none focus:bg-white ${errors.name ? "border-red" : "border-slate-300 focus:border-red"}`}
                    />
                    {errors.name && (
                      <p className="text-red text-xs px-1">{errors.name}</p>
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    <div
                      className={`flex items-center border rounded-lg px-3.5 py-2 sm:py-2.5 gap-2 bg-slate-50 ${errors.phone ? "border-red" : "border-slate-300"}`}
                    >
                      <span className="text-base sm:text-lg">🇮🇳</span>
                      <span className="text-xs sm:text-sm font-medium text-slate-600">
                        +91
                      </span>
                      <input
                        name="phone"
                        value={form.phone}
                        onChange={handleFormChange}
                        placeholder="Phone Number"
                        inputMode="numeric"
                        className="bg-transparent flex-1 text-xs sm:text-sm placeholder-gray-400 focus:outline-none"
                      />
                    </div>
                    {errors.phone && (
                      <p className="text-red text-xs px-1">{errors.phone}</p>
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    <input
                      name="email"
                      value={form.email}
                      onChange={handleFormChange}
                      placeholder="Your Email-Id"
                      className={`bg-slate-50 border rounded-lg px-3.5 py-2 sm:py-2.5 text-xs sm:text-sm placeholder-gray-400 focus:outline-none focus:bg-white ${errors.email ? "border-red" : "border-slate-300 focus:border-red"}`}
                    />
                    {errors.email && (
                      <p className="text-red text-xs px-1">{errors.email}</p>
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    <textarea
                      name="address"
                      value={form.address}
                      onChange={handleFormChange}
                      placeholder="Your Address"
                      rows={2}
                      className={`bg-slate-50 border rounded-lg px-3.5 py-2 sm:py-2.5 text-xs sm:text-sm placeholder-gray-400 focus:outline-none focus:bg-white ${errors.address ? "border-red" : "border-slate-300 focus:border-red"}`}
                    />
                    {errors.address && (
                      <p className="text-red text-xs px-1">{errors.address}</p>
                    )}
                  </div>
                </div>

                {/* Donation Details */}
                <div className="w-full md:w-1/2 border border-slate-200 bg-white rounded-xl py-3 sm:py-4 px-3.5 sm:px-4 flex flex-col gap-3 shadow-sm">
                  <h3 className="font-bold text-sm sm:text-base text-slate-900">
                    Donation Details
                  </h3>
                  <div className="flex flex-col gap-2.5 max-h-48 overflow-y-auto pr-1">
                    {donations.map((d) => (
                      <div key={d.label} className="flex items-center gap-2.5">
                        <img
                          src={d.img}
                          alt=""
                          className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg object-cover flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-xs sm:text-sm m-0 truncate text-slate-900">
                            {d.label}
                          </p>
                          <p className="text-red m-0 font-semibold text-xs mt-0.5">
                            ₹{d.price.toLocaleString("en-IN")}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <hr className="border-slate-200 my-0.5" />
                  <div className="flex justify-between font-bold text-sm sm:text-base text-slate-900">
                    <span>Total</span>
                    <span className="text-red">
                      ₹ {totalAmount.toLocaleString("en-IN")}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Continue to Pay (Always visible at bottom) */}
            <div className="pt-3 sm:pt-4 shrink-0">
              <button
                type="button"
                onClick={handleContinueToPay}
                disabled={loading}
                className="bg-red hover:bg-darkred text-white font-bold py-3 sm:py-3.5 px-6 rounded-xl text-sm sm:text-base transition-all w-full disabled:opacity-60 disabled:cursor-not-allowed shadow-md active:scale-[0.99]"
              >
                {loading
                  ? "Processing..."
                  : `Continue to Pay ₹${totalAmount.toLocaleString("en-IN")}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Janmashatmi;
